import classNames from 'classnames'

import { navigateTo } from '../../../common/frontend/route-utils.js'
import { t } from '../../../common/i18n.js'
import type { Language } from '../../../common/types/i18n.js'
import type { ThemePreference } from '../../../common/types/theme.js'
import type { NavGroup } from '../../nav-config.js'
import { navIcons } from '../../nav-icons.js'
import type { ThemeSelectHandler } from '../../utils/theme.js'
import { ExternalLink, File, LogOut, MessageSquarePlus } from '../icon/solar-icons.js'
import { Link } from '../link/link.js'
import { PortalAvatar } from '../portal-avatar/portal-avatar.js'
import type { PortalHeroLink } from '../portal-hero/portal-hero.js'
import { ThemeSwitch } from '../theme-switch/theme-switch.js'
import './portal-mobile-drawer.css'

export interface PortalMobileDrawerProps {
    isOpen: boolean
    onClose: () => void
    userName: string
    userRole: string
    avatarUrl?: string | null
    lang: Language
    activePath: string | null
    groups: NavGroup[]
    quickLinks: PortalHeroLink[]
    googleDriveUrl: string | null
    myEmployeeId: number | null
    onLanguageToggle: () => void
    onFeedbackOpen: () => void
    themePreference: ThemePreference
    onSelectTheme: ThemeSelectHandler
    themeLabels: Record<ThemePreference, string>
}

export const PortalMobileDrawer = ({
    isOpen,
    onClose,
    userName,
    userRole,
    avatarUrl,
    lang,
    activePath,
    groups,
    quickLinks,
    googleDriveUrl,
    myEmployeeId,
    onLanguageToggle,
    onFeedbackOpen,
    themePreference,
    onSelectTheme,
    themeLabels,
}: PortalMobileDrawerProps) => {
    // The profile header + footer buttons are <button>s (not links), so they
    // navigate imperatively. The nav list itself uses the self-navigating Link.
    const handleNavClick = (path: string) => {
        onClose()
        navigateTo(path)
    }

    const handleOverlayClick = (e: React.MouseEvent) => {
        if (e.target === e.currentTarget) onClose()
    }

    const driveHref = googleDriveUrl
        ? /^https?:\/\//i.test(googleDriveUrl)
            ? googleDriveUrl
            : `https://${googleDriveUrl}`
        : null

    return (
        <div
            className={classNames('portal-mobile-drawer-overlay', isOpen && 'is-open')}
            onClick={handleOverlayClick}
        >
            <nav className="portal-mobile-drawer">
                {/* Profile header */}
                <div className="portal-mobile-drawer__header">
                    <button
                        type="button"
                        className="portal-mobile-drawer__profile"
                        onClick={() => {
                            if (myEmployeeId) handleNavClick(`/people/${myEmployeeId}`)
                        }}
                    >
                        <PortalAvatar name={userName} avatarUrl={avatarUrl} px={40} />
                        <div>
                            <div className="portal-mobile-drawer__profile-name">{userName}</div>
                            <div className="portal-mobile-drawer__profile-role">{userRole}</div>
                        </div>
                    </button>
                </div>

                {/* Main nav — driven by the shared nav-config groups */}
                <div className="portal-mobile-drawer__nav">
                    {groups.map((group, groupIndex) => (
                        <div key={group.label?.(lang) ?? groupIndex}>
                            {groupIndex > 0 && <div className="portal-mobile-drawer__divider" />}
                            {group.label && (
                                <div className="portal-mobile-drawer__section-label">
                                    {group.label(lang)}
                                </div>
                            )}
                            {group.items.map((item) => {
                                if (item.external) {
                                    if (item.path === 'google-drive-external') {
                                        if (!googleDriveUrl) return null
                                        return (
                                            <Link
                                                key={item.path}
                                                href={driveHref || '#'}
                                                external
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="portal-mobile-drawer__nav-link"
                                                onClick={onClose}
                                            >
                                                <span className="portal-mobile-drawer__nav-icon">
                                                    {navIcons[item.path] ?? <File size={18} />}
                                                </span>
                                                {item.label(lang)}
                                                <span className="portal-mobile-drawer__nav-external">
                                                    ↗
                                                </span>
                                            </Link>
                                        )
                                    }
                                    return null
                                }

                                return (
                                    <Link
                                        key={item.path}
                                        href={item.path}
                                        className="portal-mobile-drawer__nav-link"
                                        isActive={activePath === item.path}
                                        onClick={onClose}
                                    >
                                        <span className="portal-mobile-drawer__nav-icon">
                                            {navIcons[item.path] ?? <File size={18} />}
                                        </span>
                                        {item.label(lang)}
                                    </Link>
                                )
                            })}
                        </div>
                    ))}

                    {/* External links ("väliviited") — mirrors the desktop "Lingid" menu */}
                    {quickLinks.length > 0 && (
                        <div>
                            <div className="portal-mobile-drawer__divider" />
                            <div className="portal-mobile-drawer__section-label">
                                {t.portal.nav.links(lang)}
                            </div>
                            {quickLinks.map((link) => (
                                <Link
                                    key={link.url}
                                    href={link.url || '#'}
                                    external
                                    target={link.openInNewTab ? '_blank' : undefined}
                                    rel="noopener noreferrer"
                                    className="portal-mobile-drawer__nav-link"
                                    onClick={onClose}
                                >
                                    <span className="portal-mobile-drawer__nav-icon">
                                        <ExternalLink size={18} />
                                    </span>
                                    {link.label}
                                    <span className="portal-mobile-drawer__nav-external">↗</span>
                                </Link>
                            ))}
                        </div>
                    )}
                </div>

                {/* Footer actions */}
                <div className="portal-mobile-drawer__footer">
                    <div className="portal-mobile-drawer__theme">
                        <span className="portal-mobile-drawer__theme-label">
                            {t.theme.label(lang)}
                        </span>
                        <ThemeSwitch
                            value={themePreference}
                            onChange={onSelectTheme}
                            labels={themeLabels}
                            size="compact"
                        />
                    </div>
                    <button
                        type="button"
                        className="portal-mobile-drawer__footer-btn"
                        onClick={() => {
                            onClose()
                            onLanguageToggle()
                        }}
                    >
                        {t.portal.nav.changeLanguage(lang)} ({lang === 'et' ? 'EN' : 'ET'})
                    </button>
                    <button
                        type="button"
                        className="portal-mobile-drawer__footer-btn"
                        onClick={() => {
                            onClose()
                            onFeedbackOpen()
                        }}
                    >
                        <MessageSquarePlus size={16} />
                        {t.feedback.triggerLabel(lang)}
                    </button>
                    <button
                        type="button"
                        className="portal-mobile-drawer__footer-btn portal-mobile-drawer__footer-btn--danger"
                        onClick={() => {
                            onClose()
                            void fetch('/api/logout', { method: 'POST' }).then(() => {
                                window.location.href = '/'
                            })
                        }}
                    >
                        <LogOut size={16} />
                        {t.portal.nav.logOut(lang)}
                    </button>
                </div>
            </nav>
        </div>
    )
}
